<template>
    <VJoyWrapper :direction="isDisplayMobile ? 'column' : 'row'" :align="isDisplayMobile ? 'center' : 'flex-start'">
        <div class="profile-common-resume-import-upload__column">
            <VJoyDropzone
                class="profile-common-resume-import-upload__dropzone"
                data-testid="profile-common-resume-import-upload-dropzone"
                name="profileUploadResume"
                :accept="ruleDropzoneFileFormats"
                :error-text-max-size="$t('general.errors.upload.file.maxsize', [`${ruleDropzoneMaxFileSize}`])"
                :max-size="ruleDropzoneMaxFileSize"
                :model-value="dropzoneFiles"
                @update:model-value="(value: File[]) => (dropzoneFiles = value)"
            >
                <template #dropzone-description>{{ $t('profile.experience.funnel.step.upload.dropzone.description') }}</template>
                <template #dropzone-legend>
                    {{ $t('profile.experience.funnel.step.upload.dropzone.formats') }} {{ acceptedFileFormatNames.join(', ') }}
                </template>
            </VJoyDropzone>
        </div>
        <div class="profile-common-resume-import-upload__column">
            <div class="profile-common-resume-import-upload__right-panel">
                <VJoyWrapper class="profile-common-resume-import-upload__header" wrap="nowrap" column-gap="2" align="flex-start" :no-margin="true">
                    <MaltImg src="/profile/edition/linkedin-logo.svg" alt="Linkedin logo" width="24" height="24" />

                    <VJoyWrapper direction="column" row-gap="0" align="flex-start" :no-margin="true">
                        <span class="joy-font-primary-450">
                            {{ $t('profile.experience.funnel.step.upload.dropzone.linkedin.explanation.title') }}
                        </span>
                        <span>
                            {{ $t('profile.experience.funnel.step.upload.dropzone.linkedin.explanation.subtitle') }}
                            <VJoyLink href="https://www.linkedin.com/in/" target="_blank" rel="noopener noreferrer">
                                {{ $t('profile.experience.funnel.step.upload.dropzone.linkedin.explanation.subtitle.link') }}
                            </VJoyLink>
                        </span>
                    </VJoyWrapper>
                </VJoyWrapper>

                <MaltImg
                    class="profile-common-resume-import-upload__image"
                    :src="imgUrlHelpLinkedInExport"
                    alt="Linkedin import explanation"
                    width="326"
                />
            </div>
        </div>
    </VJoyWrapper>
</template>
<script setup lang="ts">
    import {computed, ref, toRefs, watch} from 'vue';
    import {MaltImg} from '#components';
    import {useLocale} from '#imports';
    import {useDisplayMobile} from '@malt/utils';
    import {VJoyDropzone, VJoyWrapper, VJoyLink} from '@maltjoy/core-vue';

    type ResumeImportUploadFileProps = {
        modelValue?: File;
        ruleDropzoneFileFormats?: string[];
        ruleDropzoneMaxFileSize?: number;
    };

    const props = withDefaults(defineProps<ResumeImportUploadFileProps>(), {
        modelValue: undefined,
        ruleDropzoneFileFormats: () => ['application/pdf'],
        ruleDropzoneMaxFileSize: 5,
    });

    const emit = defineEmits<{
        (e: 'update:model-value', value: File | undefined): void;
    }>();

    // Composables
    const {locale} = useLocale();

    // Refs
    const {ruleDropzoneFileFormats, ruleDropzoneMaxFileSize} = toRefs(props);
    const dropzoneFiles = ref<File[]>([]);

    // Computed
    const {isDisplayMobile} = useDisplayMobile('Xs');
    const dropzoneSingleFile = computed((): File | undefined => (dropzoneFiles.value.length > 0 ? dropzoneFiles.value[0] : undefined));
    const isFileValid = computed(
        (): boolean =>
            !!dropzoneSingleFile.value &&
            ruleDropzoneFileFormats.value.includes(dropzoneSingleFile.value.type) &&
            dropzoneSingleFile.value.size <= ruleDropzoneMaxFileSize.value * 1024 * 1024,
    );
    const acceptedFileFormatNames = computed(() => ruleDropzoneFileFormats.value.map((format) => format.split('/')[1]));
    const imgUrlHelpLinkedInExport = computed(() => {
        const existingTranslations = ['de', 'en', 'es', 'fr', 'nl'];
        const currentLanguage = getCurrentLanguage() || 'en';

        const imageUrl = '/profile/edition/linkedin_resume_v2';
        const language = existingTranslations.includes(currentLanguage) ? currentLanguage : 'en';
        return `${imageUrl}_${language}.png`;
    });

    // Watcher
    watch(dropzoneSingleFile, () => {
        const file = dropzoneSingleFile.value && isFileValid.value ? dropzoneSingleFile.value : undefined;
        emit('update:model-value', file);
    });

    // Functions
    function getCurrentLanguage(): string {
        return locale.value.split('-')[0];
    }
</script>
<style lang="scss">
    .profile-common-resume-import-upload {
        &__column {
            flex: 1;
            min-width: 276px;
        }

        &__right-panel {
            border-radius: var(--joy-core-radius-3);
            position: relative;
            border: 1px solid var(--joy-color-neutral-30);
            min-height: 315px;
        }

        &__header {
            margin: var(--joy-core-spacing-4) var(--joy-core-spacing-4) var(--joy-core-spacing-2) var(--joy-core-spacing-4) !important;
        }

        &__dropzone {
            .joy-dropzone__instruction {
                height: 312px;
                flex-direction: column;
                justify-content: center;
            }

            .joy-dropzone__file {
                height: 280px;
            }
        }
        &__image {
            width: 100%;
            margin-left: var(--joy-core-spacing-2);
        }
    }
</style>
